import { publicEnv } from '@/lib/env';
import {
  EventType,
  PlayerUpcomingScheduleEvent,
  PlayOpportunitiesApiResponse,
  PlayOpportunityItem,
  SystemModuleFor,
} from '@/modules/players/v3/types/events';
import { GAME_MAKER_DEEP_LINK_HOST } from '@/utils/helpers/constants';

const CLUB_MODULE_IDS = new Set<number>([
  SystemModuleFor.CLUB_EVENT_SESSION,
  SystemModuleFor.CLUB_PRIVATE_PLAY_SESSION,
  SystemModuleFor.CLUB_LESSON_SESSION,
]);

// Game Maker reaches us under three ids: a scheduled game, a game day, and a manually recorded one.
const GAME_MAKER_MODULE_IDS = new Set<number>([
  SystemModuleFor.PICKUP_GAME,
  SystemModuleFor.PICKUP_GAME_DAY,
  SystemModuleFor.MANUALLY_RECORDED_GAME,
]);

export const moduleForToEventType = (moduleFor: string | number): EventType | undefined => {
  const id = Number(moduleFor);
  switch (id) {
    case SystemModuleFor.TOURNAMENT:
      return EventType.TOURNAMENT;
    // Only matches carry module 12 — the schedule and opportunity feeds union tournaments, team
    // leagues, ladder leagues and pickup games only, so leagues need no event URL or filter there.
    case SystemModuleFor.LEAGUE:
      return EventType.LEAGUE;
    case SystemModuleFor.LADDER_LEAGUE:
      return EventType.LADDER_LEAGUE;
    case SystemModuleFor.TEAM_LEAGUE:
      return EventType.TEAM_LEAGUE;
    default:
      if (GAME_MAKER_MODULE_IDS.has(id)) return EventType.PICKUP_GAME;
      if (CLUB_MODULE_IDS.has(id)) return EventType.CLUB;
      return undefined;
  }
};

export const isGameMakerModule = (moduleFor: string | number): boolean =>
  moduleForToEventType(moduleFor) === EventType.PICKUP_GAME;

export const formatDistanceMiles = (distanceMiles?: number): string => {
  if (!distanceMiles || distanceMiles <= 0) return '';
  // Rounding sub-mile distances would render "0 mi away".
  return distanceMiles < 1 ? '< 1 mi away' : `${Math.round(distanceMiles)} mi away`;
};

/**
 * destination_url is never fetched — the Game Maker app matches `/game_id=&?([^&]+)/` and
 * `/game_day_id=([^&]+)/` against it, so the `&` after `game_id=` is load-bearing and the host is
 * not. See pbgo_app src/redux/hooks/useDeepLinkHandler.ts.
 *
 * Both ids must stay inside destination_url: pbgm.io is served by apps/deeplinks, which forwards
 * only `payload = { destinationURL }` to the app and drops every other query param — so the
 * top-level `game_day_id` and `is_organiser` of the backend's own ResultsBaseURL never arrive.
 */
export const getGameMakerGameUrl = (gameId: string, gameDayId: string): string => {
  const destination = `https://${publicEnv.NEXT_PUBLIC_HOME_ROOT_URL}/gameShare/?game_id=&${gameId}&game_day_id=${gameDayId}`;
  return `${GAME_MAKER_DEEP_LINK_HOST}?destination_url=${encodeURIComponent(destination)}`;
};

// Game Maker is absent on purpose — its games are deep links, not `${baseUrl}/${path}/${id}` pages.
const SCHEDULE_EVENT_CONFIG: Partial<Record<SystemModuleFor, { baseUrl: string; path: string }>> = {
  [SystemModuleFor.TOURNAMENT]: {
    baseUrl: publicEnv.NEXT_PUBLIC_PT_URL,
    path: 'tournaments',
  },
  [SystemModuleFor.LADDER_LEAGUE]: {
    baseUrl: publicEnv.NEXT_PUBLIC_PL_URL,
    path: 'leagues',
  },
  [SystemModuleFor.TEAM_LEAGUE]: {
    baseUrl: publicEnv.NEXT_PUBLIC_PTL_URL,
    path: 'team-leagues',
  },
  [SystemModuleFor.CLUB_EVENT_SESSION]: {
    baseUrl: publicEnv.NEXT_PUBLIC_PC_URL,
    path: 'clubs',
  },
  [SystemModuleFor.CLUB_PRIVATE_PLAY_SESSION]: {
    baseUrl: publicEnv.NEXT_PUBLIC_PC_URL,
    path: 'clubs',
  },
  [SystemModuleFor.CLUB_LESSON_SESSION]: {
    baseUrl: publicEnv.NEXT_PUBLIC_PC_URL,
    path: 'clubs',
  },
};

export const getScheduleEventUrl = (
  event: Pick<PlayerUpcomingScheduleEvent, 'moduleFor' | 'moduleUuid' | 'uuid' | 'slug'>
): { href: string; badgeType?: EventType } => {
  const badgeType = moduleForToEventType(event.moduleFor);

  if (badgeType === EventType.PICKUP_GAME) {
    return { href: getGameMakerGameUrl(event.moduleUuid, event.uuid), badgeType };
  }

  const config = SCHEDULE_EVENT_CONFIG[event.moduleFor];
  if (!config) return { href: '#', badgeType };

  return { href: `${config.baseUrl}/${config.path}/${event.slug || event.uuid}`, badgeType };
};

export const getOpportunityEventUrl = (
  moduleFor: string,
  moduleUuid: string,
  slug?: string,
  uuid?: string
): string => {
  const pathId = slug || moduleUuid;
  const type = moduleForToEventType(moduleFor);
  switch (type) {
    case EventType.TOURNAMENT:
      return `${publicEnv.NEXT_PUBLIC_PT_URL}/tournaments/${pathId}`;
    case EventType.LADDER_LEAGUE:
      return `${publicEnv.NEXT_PUBLIC_PL_URL}/leagues/${pathId}`;
    case EventType.TEAM_LEAGUE:
      return `${publicEnv.NEXT_PUBLIC_PTL_URL}/team-leagues/${pathId}`;
    case EventType.CLUB:
      return `${publicEnv.NEXT_PUBLIC_PC_URL}/clubs/${pathId}`;
    case EventType.PICKUP_GAME:
      return getGameMakerGameUrl(moduleUuid, uuid ?? '');
    default:
      return '#';
  }
};

export const groupOpportunitiesByType = (
  playOpportunities: PlayOpportunitiesApiResponse | undefined
): Record<EventType, PlayOpportunityItem[]> => {
  const groups: Record<EventType, PlayOpportunityItem[]> = {
    [EventType.TOURNAMENT]: [],
    [EventType.LEAGUE]: [],
    [EventType.LADDER_LEAGUE]: [],
    [EventType.TEAM_LEAGUE]: [],
    [EventType.CLUB]: [],
    [EventType.PICKUP_GAME]: [],
  };

  const opportunities = playOpportunities?.results?.opportunities ?? [];
  for (const opportunity of opportunities) {
    const type = moduleForToEventType(opportunity.moduleFor);
    if (type) {
      groups[type].push(opportunity);
    }
  }

  return groups;
};

/**
 * Round-robin one card per module so a module with nothing nearby doesn't leave a gap, then sort by
 * date — the selection decides which items make the cut, never the order they're shown in.
 */
export const balanceOpportunitiesByModule = (
  playOpportunities: PlayOpportunitiesApiResponse | undefined,
  perModule: number
): PlayOpportunityItem[] => {
  const groups = groupOpportunitiesByType(playOpportunities);
  const balanced: PlayOpportunityItem[] = [];

  for (let pass = 0; pass < perModule; pass++) {
    for (const type of Object.values(EventType)) {
      const item = groups[type][pass];
      if (item) balanced.push(item);
    }
  }

  return balanced.sort((a, b) => a.startDate.localeCompare(b.startDate));
};

export type UpcomingScheduleDayGroup = {
  key: string;
  label: string;
  events: PlayerUpcomingScheduleEvent[];
};

export const groupSchedulesByDay = (
  schedules: PlayerUpcomingScheduleEvent[]
): UpcomingScheduleDayGroup[] => {
  const sorted = [...schedules].sort((a, b) => a.startDate.localeCompare(b.startDate));
  const groups: UpcomingScheduleDayGroup[] = [];
  const indexByKey = new Map<string, number>();

  for (const event of sorted) {
    const key = event.startDateLabel;
    const existingIndex = indexByKey.get(key);
    const existingGroup = existingIndex === undefined ? undefined : groups[existingIndex];

    if (existingGroup) {
      existingGroup.events.push(event);
    } else {
      indexByKey.set(key, groups.length);
      groups.push({ key, label: event.startDateLabel, events: [event] });
    }
  }

  return groups;
};
